<template>
  <CardSection :loading="isSavableStateLoading"
    :error="majorError">
    <template #header>
      Comms Templates
    </template>

    <template #body>
      <div v-if="formErrorMessage" class="alert alert-danger" role="alert">
        {{ formErrorMessage }}
      </div>
      <PortalTable ref="template_index" table-id="template_index"
        :table-data="records"
        :page="pageNumber"
        :page-count="pageCount"
        :loading="isSavableStateProcessing"
        :filter-values="filterValues"
        filter-display="row"
        empty="No Templates found!"
        @on-sort="handleSortChange"
        @on-page="handlePageChange"
        @on-filter-change="onFilterChange">
        <template #columns>
          <Column field="category_name" 
            header="Template Category" 
            sort-field="category_name"
            :sortable="true"
            filter-field="category_name" 
            :show-filter-menu="false">
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <InputText v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search by Category Name"
                @input="filterCallback()" />
            </template>
          </Column>
          <Column field="generated_at" header="Generated At">
            <template #body="slotProps">
              {{ slotProps.data.instance ? new Date(slotProps.data.instance.created_at).toLocaleString() : '' }}
            </template>
          </Column>
          <Column field="last_saved" header="Last Saved">
            <template #body="slotProps">
              {{ slotProps.data.instance ? (slotProps.data.instance.last_saved ? new Date(slotProps.data.instance.last_saved.created_at).toLocaleString() : 'N/A') : '' }}
            </template>
          </Column>
          <Column field="last_reset" header="Last Reset">
            <template #body="slotProps">
              {{ slotProps.data.instance ? new Date(slotProps.data.instance.effective_last_reset).toLocaleString() : '' }}
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <button class="btn btn-success" @click.prevent="open(slotProps.data.instance.id)" v-if="slotProps.data.instance">
                Open Template
              </button>
              <button class="btn btn-success" @click.prevent="generate(slotProps.data)" v-else>
                Generate Template
              </button>
            </template>
          </Column>
        </template>
      </PortalTable>

    </template>
  </CardSection>
</template>

<script lang="js">
import CardSection from "@/src/components/shared/CardSection.vue";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import { useTemplateStore } from '@/src/stores/template';
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import { Form as VeeForm } from 'vee-validate';
import '@/src/vee-validate.js';
import { pageMixin } from "@/src/mixins/pageMixin";
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
import {APIRoute, EP} from "@/src/endpoints";
import beApiClient from "@/src/be-api-client";
import { storeToRefs } from 'pinia'
import {useAppCoreStore} from "@/src/stores/app-core";
import {urlParams} from "~/src/endpoints";

import InputText from 'primevue/inputtext';
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";
import { paginationMixin } from "@/src/mixins/paginationMixin";
import { FilterMatchMode } from '@primevue/core/api';

export default {
  components: {
    CardSection,
    ActionToolbar,
    VeeForm,
    MultiSelectInput,
    PortalTable,
    Column,
    InputText,
    MultiSelectFilter
  },
  mixins: [ pageMixin, savableStateMixin, paginationMixin ],
  setup() {
    const templateStore = useTemplateStore();
    const appStore = useAppCoreStore();
    const { getTemplateList, translateOrganGroup, translateTxcCode } = storeToRefs(templateStore)
    return {
      templateStore, getTemplateList, appStore, translateOrganGroup, translateTxcCode
    }
  },
  data() {
    return {
      filterValues: {
        "category_name": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      },
      loading: true,
      communicationTemplateCategoryId: null,
      formErrorMessage: null,
      majorError: null,
      donorDetails: null,
      matchDetails: null,
      txc: null,
      organGroup: null,
      match: null,
      donor: null
    }
  },
  async mounted() {
    try {
      this.setSavableStateLoading();
      // get donor & match details
      await this.templateStore.loadTransplantCentres();
      await this.templateStore.loadOrganGroups();

      const matchDetails = await beApiClient.get(APIRoute(EP.matches.show, { id: this.$route.params.match_id }));
      // TODO: Clean all this up and have cleaner API endpoints for this
      // TODO: Switch to using the Match.ts class
      this.match = this.$route.params.match_id;
      this.matchDetails = matchDetails.data;
      this.donor = this.matchDetails.donor_id;
      this.txc = this.matchDetails.transplant_center_id;
      this.organGroup = this.matchDetails.organ.organ_group_id;
      const donorDetails = await beApiClient.get(APIRoute(EP.donors.show, { id: this.matchDetails.donor_id }));
      this.donorDetails = donorDetails.data;
      if (!donorDetails.data || !matchDetails.data) { this.setSavableStateErrored(); return false; }
      
      this.setupBreadCrumb();
      this.setSavableStateIdle();
    } catch(error) {
      this.handleMajorError(error);

    }
    await this.getRowData();
  },
  methods: {
    // build breadcrumb
    setupBreadCrumb() {
      this.appStore.setAppBreadcrumbs([
        {
          to: { name: 'internal-dashboards-root' },
          text: 'Internal'
        },
        {
          to: { name: 'internal-dashboard-plugin' },
          text: 'Dashboard'
        },
        {
          to: { 
            name: 'internal-match-comms-templates', 
            params: { match_id: this.matchDetails.id }
          },
          text: this.generateBreadCrumb(
            this.donorDetails.donor.code,
            this.matchDetails.code,
            this.translateTxcCode(this.txc),
            this.translateOrganGroup(this.organGroup)
          )
        }
      ]);
    },
    getCommunicationTemplateIdFromCategory(id) {
      const found = this.templateStore.categories.find((item) => {
        return item.id === id;
      })
      return found && found.communication_templates ? found.communication_templates.find(t => !t.disabled_at)?.id : null;
    },
    async generate(data) {
      try {
        this.setSavableStateProcessing()

        const communication_template_id = data.communication_templates.find(t => !t.disabled_at).id;

        // generate initial body
        const response = await this.templateStore.generateInstanceBody(communication_template_id, this.match);

        // build payload to generate first instance of template
        const payload = {
          communication_template_id: communication_template_id,
          instance_text: response.template_body
        }

        // generate first instance of template
        const responseOnSave = await this.templateStore.generateTemplateInstanceFromMatch(this.match, payload)
        const instanceId = responseOnSave.id;

        // set success save state
        this.setSavableStateSuccess();

        // now successfully created instance, go to edit page
        this.$router.push({ name: 'edit-instance',
          params: {
            donor: this.donor, 
            match: this.match, 
            txc: this.txc, 
            organGroup: this.organGroup,
            instanceId: instanceId
          }
        });
      } catch (error) {
        this.formErrorMessage = error;
        this.setSavableStateErrored();
      }
    },
    async open(instanceId) {
      this.setSavableStateProcessing()

      // switch to edit page
      this.$router.push({ name: 'edit-instance',
        params: {
          donor: this.donor, 
          match: this.match, 
          txc: this.txc, 
          organGroup: this.organGroup,
          instanceId: instanceId
        }
      });
    },
    async getRowData(reset = false) {
      this.setSavableStateProcessing();
      if (reset) this.pageNumber = 1;
     
      if (this.currentSortField) {
        this.sort_params = urlParams({ sort_by: this.currentSortField, sort_type: this.currentSortType });
      } else {
        this.sort_params = '';
      }

      let params = {
        page: this.pageNumber,
        sort_by: this.currentSortField,
        sort_type: this.currentSortType
      };

      params = { ...this.search_params, ...params };

      try {
        const response = await this.templateStore.loadCategoryTypesByOrganGroupAndTxc(this.organGroup, this.txc, params);
        const categories = response.data || [];

        await Promise.all(  
          categories.map(async (category, index) => {
            const instances = await this.templateStore.latestTemplateInstance(this.match, category.id);
            categories[index] = { ...category, instance: instances[0] || null };
          })
        );

        // Hide categories with a disabled template (should only be one template) and no existing instances.
        const filtered = categories.filter(category => {
          const hasEnabledTemplate = category.communication_templates.some(t => !t.disabled_at);
          return hasEnabledTemplate || !!category.instance;
        });

        response.data = filtered;
        this.records.data = filtered;
        this.processPagyResponse(response);
        this.setSavableStateIdle();
      } catch(error) {
        this.handleMajorError(error);
      }
    },
    onFilterChange(event) {
      this.search_params = event;
      this.getRowData(true);
    },
    onChange(event) {
      this.$refs.template_index.resetFilters(event);
    }
  }
}
</script>

